// ===============================================================
// ⭐ PendingDeletePage.js — Account Pending Deletion (Server-only)
// ===============================================================

import React, { useEffect, useState } from "react";
import "./PendingDeletePage.css";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { auth, db, functions, httpsCallable } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import TopBar from "../components/common/TopBar";
import LivoraToast from "../components/common/LivoraToast";
import useLivoraToast from "../hooks/useLivoraToast";

function PendingDeletePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = auth.currentUser;

  const { toast, showToast, hideToast } = useLivoraToast();

  const [daysLeft, setDaysLeft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(false);

  // ============================================================
  // 🔥 Load pending delete info
  // ============================================================
  useEffect(() => {
    const loadDeleteInfo = async () => {
      try {
        if (!user) {
          navigate("/");
          return;
        }

        const userRef = doc(db, "users", user.uid);
        const snap = await getDoc(userRef);

        if (!snap.exists()) {
          navigate("/");
          return;
        }

        const data = snap.data() || {};

        if (data.accountStatus !== "pending_delete" || !data.deleteAt) {
          navigate("/home");
          return;
        }

        const deleteAt =
          typeof data.deleteAt?.toDate === "function"
            ? data.deleteAt.toDate()
            : new Date(data.deleteAt);

        const now = new Date();
        const diffMs = deleteAt - now;
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

        setDaysLeft(diffDays > 0 ? diffDays : 0);
      } catch (err) {
        console.error("Error loading delete info:", err);
        showToast(
          "error",
          t("common.general_error", {
            defaultValue: "Something went wrong. Please try again later.",
          })
        );
      } finally {
        setLoading(false);
      }
    };

    loadDeleteInfo();
  }, [navigate, user, showToast, t]);

  // ============================================================
  // 🔄 Restore account (server-only)
  // ============================================================
  const handleRestore = async () => {
    try {
      if (!user) {
        navigate("/");
        return;
      }

      setRestoring(true);

      const cancelAccountDeletion = httpsCallable(
        functions,
        "cancelAccountDeletion"
      );

      await cancelAccountDeletion();

      showToast(
        "success",
        t("account.restore_success", {
          defaultValue: "Account restored successfully.",
        })
      );

      setTimeout(() => {
        navigate("/home");
      }, 700);
    } catch (err) {
      console.error("Error restoring account:", err);
      showToast(
        "error",
        t("common.general_error", {
            defaultValue: "Something went wrong. Please try again later.",
          })
      );
    } finally {
      setRestoring(false);
    }
  };

  // ============================================================
  // ⏳ Loading UI
  // ============================================================
  if (loading) {
    return (
      <div className="pending-page">
        <div className="pending-box">
          <p>{t("common.loading", { defaultValue: "Loading..." })}</p>
        </div>
        <LivoraToast toast={toast} onClose={hideToast} />
      </div>
    );
  }

  // ============================================================
  // 🎨 UI
  // ============================================================
  return (
    <div className="pending-page">
      <TopBar
        title={t("account.pending_delete_title", {
          defaultValue: "Account pending deletion",
        })}
        onBack={() => navigate(-1)}
      />

      <div className="pending-box">
        <h2 className="pending-title">
          {t("account.pending_delete_title", {
          defaultValue: "Account pending deletion",
        })}
        </h2>

        <p className="pending-text">
          {t("account.pending_delete_text", {
            defaultValue:
              "Your account deletion request has been registered. You can restore your account before the grace period ends.",
          })}
        </p>

        <p className="days-left">
          {daysLeft === 0
            ? t("account.less_than_24_hours", {
                defaultValue: "Less than 24 hours remaining",
              })
            : t("account.days_left", {
                count: daysLeft,
                defaultValue:
                  daysLeft === 1
                    ? "1 day remaining"
                    : "{{count}} days remaining",
              })}
        </p>

        <p className="pending-warning">
          {t("account.pending_delete_warning", {
            defaultValue:
              "If you do not restore the account during this period, it will be permanently deleted.",
          })}
        </p>

        <button
          className="restore-btn"
          onClick={handleRestore}
          disabled={restoring}
        >
          {restoring
            ? t("account.restoring_account", {
                defaultValue: "Restoring account...",
              })
            : t("account.restore_account", {
                defaultValue: "Restore account",
              })}
        </button>
      </div>

      <LivoraToast toast={toast} onClose={hideToast} />
    </div>
  );
}

export default PendingDeletePage;
